/**
 * Persists a stable per-browser UUID + the user's saved-trip slugs in localStorage.
 *
 * No login required — this is purely a client-side identifier so the backend's
 * /api/ai-search/trips endpoint can associate saved itineraries with a returning
 * visitor for the "My saved trips" homepage hook.
 */
import { useCallback, useEffect, useState } from 'react';

const UUID_KEY = 'wfa_user_uuid';
const TRIPS_KEY = 'wfa_my_trips';

function generateUuid() {
  // RFC4122 v4 — uses crypto.randomUUID when available, falls back to Math.random.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function readTrips() {
  try {
    const raw = localStorage.getItem(TRIPS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function useUserUuid() {
  const [uuid, setUuid] = useState('');
  const [myTrips, setMyTrips] = useState([]);

  useEffect(() => {
    let existing = '';
    try {
      existing = localStorage.getItem(UUID_KEY) || '';
    } catch {
      existing = '';
    }
    if (!existing) {
      existing = generateUuid();
      try {
        localStorage.setItem(UUID_KEY, existing);
      } catch {
        // ignore (private mode / quota)
      }
    }
    setUuid(existing);
    setMyTrips(readTrips());
  }, []);

  const recordTrip = useCallback((trip) => {
    // trip: { slug, query, created_at }
    if (!trip || !trip.slug) return;
    setMyTrips((prev) => {
      const dedup = [trip, ...prev.filter((t) => t.slug !== trip.slug)].slice(0, 20);
      try {
        localStorage.setItem(TRIPS_KEY, JSON.stringify(dedup));
      } catch {
        // ignore
      }
      return dedup;
    });
  }, []);

  const removeTrip = useCallback((slug) => {
    if (!slug) return;
    setMyTrips((prev) => {
      const next = prev.filter((t) => t.slug !== slug);
      try {
        localStorage.setItem(TRIPS_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  return { uuid, myTrips, recordTrip, removeTrip };
}
